import cx from 'classnames';

import { Icon, IconInfo } from '@flixbus/honeycomb-icons-react';
import { Box, Divider, Heading, Tooltip } from '@flixbus/honeycomb-react';

import JobList from 'common/JobList';
import RunItem from 'common/RunItem';

import EmploymentControls from '../EmploymentControls';
import { crewMemberInfoText } from '../text';
import { buildHoursTrackingRows } from './buildHoursTrackingRows';
import { deriveClassification } from './deriveClassification';
import { deriveEmploymentTypeLabel } from './deriveEmploymentTypeLabel';

import styles from './styles.module.css';

const WorkingInfo = ({ employeeInfo, employmentType }) => {
	if (employeeInfo == null) return null;

	const { driverLicenseInfo, medicalCheckInfo, run, jobs, currentRun, hoursTracking } =
		employeeInfo;
	const employmentTypeLabel = deriveEmploymentTypeLabel(employmentType);
	const classificationLabel = deriveClassification(currentRun);

	return (
		<Box extraClasses={styles.block}>
			<div className={cx(styles.blockItem, styles.columns)}>
				<EmploymentControls />
				<div>
					<Heading size={3} sectionHeader>
						{crewMemberInfoText.employmentType}
					</Heading>
					<div>{employmentTypeLabel}</div>
					<div className={styles.small}>
						{`${crewMemberInfoText.driverLicense}: ${driverLicenseInfo}`}
					</div>
				</div>
				<div>
					<Heading size={3} sectionHeader>
						{crewMemberInfoText.classification}
					</Heading>
					<div>{classificationLabel}</div>
					<div className={styles.small}>
						{`${crewMemberInfoText.medicalCheck}: ${medicalCheckInfo}`}
					</div>
				</div>
			</div>

			<Divider />

			<div className={cx(styles.blockItem, styles.columns, styles.between)}>
				<div>
					<Heading size={3} sectionHeader>
						{crewMemberInfoText.workingCycle}
					</Heading>
					<div className={styles.workingCycleWrapper}>
						<div className={cx(styles.blockRow, styles.start, styles.smallGap)}>
							<RunItem run={run} />
						</div>
						<div className={cx(styles.blockRow, styles.start, styles.smallGap)}>
							<JobList jobs={jobs} isBadge />
						</div>
					</div>
				</div>

				{hoursTracking && (
					<div>
						<div className={styles.hoursTrackingHeader}>
							<Heading size={3} sectionHeader flushSpace>
								{crewMemberInfoText.hoursTracking}
							</Heading>
							<Tooltip
								id="hours-tracking-tooltip"
								openOnHover
								position="top"
								size="content-fit"
								content={
									<div className={styles.hoursTrackingTooltip}>
										<div>
											<strong>{crewMemberInfoText.drivingBalance}</strong>
											<div>{crewMemberInfoText.drivingBalanceTooltip}</div>
										</div>
										<div>
											<strong>{crewMemberInfoText.dutyBalance}</strong>
											<div>{crewMemberInfoText.dutyBalanceTooltip}</div>
										</div>
										<div>
											<strong>{crewMemberInfoText.eightDayDuty}</strong>
											<div>{crewMemberInfoText.eightDayDutyTooltip}</div>
										</div>
									</div>
								}
							>
								<Icon size={3} extraClasses={styles.trackingHoursTooltip} InlineIcon={IconInfo} />
							</Tooltip>
						</div>

						<div className={styles.hoursTrackingRows}>
							{buildHoursTrackingRows(hoursTracking).map((row) => (
								<div
									key={row.key}
									className={styles.hoursTrackingRow}
									data-testid={`hours-tracking-row-${row.key}`}
								>
									<span>{row.label}</span>
									<span
										data-testid={`hours-tracking-badge-${row.key}`}
										className={cx(styles.hoursTrackingBadge, {
											[styles.hoursTrackingBadgeWarning]: row.isWarning,
										})}
									>
										{row.value}
									</span>
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</Box>
	);
};

export default WorkingInfo;
